<template>
  <div>
    <b-modal
      v-model="show"
      size="lg"
      :title="mode === 'edit' ? 'Edit Company Client' : 'Create Company Client'"
      hide-footer
      @hide="onHide"
    >
      <div class="company-client-modal">
        <div class="company-client-modal__intro">
          <div class="company-client-modal__eyebrow">Company Client</div>
          <div class="company-client-modal__lede">
            {{
              mode === "edit"
                ? "Update company-client profile, contact point, and WPS policy information."
                : "Create a new company client under this parent company with profile, agreement, and WPS policy data."
            }}
          </div>
        </div>

        <b-form @submit.prevent="onSubmit">
          <section class="company-client-modal__section">
            <div class="company-client-modal__section-head">
              <h4>Profile</h4>
              <p>Basic company information and primary contact details.</p>
            </div>
            <div class="company-client-modal__grid">
              <div class="ds-field company-client-modal__field-full">
                <label class="ds-field__label">Company Name</label>
                <el-input v-model="form.name" size="small" placeholder="Input company name" />
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Phone</label>
                <el-input v-model="form.phone" size="small" placeholder="Input phone number" type="number" />
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Email</label>
                <el-input v-model="form.email" size="small" placeholder="Input email" type="email" />
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Industry</label>
                <el-input v-model="form.industry" size="small" placeholder="Input industry" />
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Account Manager (AM)</label>
                <el-input v-model="form.account_manager" size="small" placeholder="Input account manager" />
              </div>

              <div v-if="mode === 'create'" class="ds-field company-client-modal__field-full">
                <label class="ds-field__label">Company Status</label>
                <el-select v-model="form.status" size="small" placeholder="Select company status" filterable class="w-100">
                  <el-option v-for="item in companyCategoryOptions" :key="item.value" :label="item.text" :value="item.value" />
                </el-select>
              </div>
            </div>
          </section>

          <section class="company-client-modal__section">
            <div class="company-client-modal__section-head">
              <h4>Location</h4>
              <p>Registered address and geographical details.</p>
            </div>
            <div class="company-client-modal__grid">
              <div class="ds-field company-client-modal__field-full">
                <label class="ds-field__label">Location</label>
                <el-input v-model="form.location" size="small" placeholder="Input location" />
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Latitude</label>
                <el-input v-model="form.latitude" size="small" placeholder="Input latitude" type="number" />
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Longitude</label>
                <el-input v-model="form.longitude" size="small" placeholder="Input longitude" type="number" />
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Province</label>
                <el-select v-model="form.province" placeholder="Select province" class="w-100" filterable>
                  <el-option
                    v-for="item in province.rows"
                    :key="item.id_province || item.province_name"
                    :label="item.province_name"
                    :value="item.id_province"
                  />
                </el-select>
              </div>

              <div class="ds-field">
                <label class="ds-field__label">City</label>
                <el-select v-model="form.city" placeholder="Select city" class="w-100" filterable>
                  <el-option
                    v-for="item in filteredCities"
                    :key="item.id_city || item.city_name"
                    :label="item.city_name"
                    :value="item.id_city"
                  />
                </el-select>
              </div>

              <div class="ds-field company-client-modal__field-full">
                <label class="ds-field__label">Address</label>
                <el-input v-model="form.address" size="small" type="textarea" :rows="3" placeholder="Input address" />
              </div>
            </div>
          </section>

          <section class="company-client-modal__section">
            <div class="company-client-modal__section-head">
              <h4>PIC</h4>
              <p>Primary person in charge for day-to-day coordination.</p>
            </div>
            <div class="company-client-modal__grid">
              <div class="ds-field">
                <label class="ds-field__label">PIC Name</label>
                <el-input v-model="form.pic_name" size="small" placeholder="Input PIC name" />
              </div>

              <div class="ds-field">
                <label class="ds-field__label">PIC Email</label>
                <el-input v-model="form.pic_email" size="small" placeholder="Input PIC email" type="email" />
              </div>

              <div class="ds-field company-client-modal__field-full">
                <label class="ds-field__label">PIC Phone</label>
                <el-input v-model="form.pic_phone_number" size="small" placeholder="Input PIC phone number" />
              </div>
            </div>
          </section>

          <section v-if="mode === 'create'" class="company-client-modal__section">
            <div class="company-client-modal__section-head">
              <h4>WPS Setup</h4>
              <p>Company policy and agreement data required for WPS operations.</p>
            </div>
            <div class="company-client-modal__grid">
              <div class="ds-field">
                <label class="ds-field__label">Max Balance Disbursement</label>
                <el-input
                  v-model="form.max_balance_disbursement"
                  size="small"
                  placeholder="Input max balance disbursement"
                  type="number"
                />
              </div>

              <div class="ds-field company-client-modal__field-full">
                <label class="ds-field__label">PKS Number</label>
                <el-input v-model="form.pks_number" size="small" placeholder="Input PKS number" />
              </div>

              <div class="ds-field">
                <label class="ds-field__label">PKS Start Date</label>
                <el-date-picker
                  :picker-options="datePickerStartOptions"
                  class="w-100"
                  v-model="form.pks_start_date"
                  type="date"
                  placeholder="Pick PKS start date"
                  format="dd MMMM yyyy"
                  value-format="yyyy-MM-dd"
                  @input="updateEndDatePickerOptions"
                />
              </div>

              <div class="ds-field">
                <label class="ds-field__label">PKS End Date</label>
                <el-date-picker
                  :picker-options="datePickerEndOptions"
                  :key="endDateKey"
                  class="w-100"
                  v-model="form.pks_end_date"
                  type="date"
                  placeholder="Pick PKS end date"
                  format="dd MMMM yyyy"
                  value-format="yyyy-MM-dd"
                />
              </div>
            </div>
          </section>

          <section class="company-client-modal__section">
            <div class="company-client-modal__section-head">
              <h4>Branding</h4>
              <p>Optional company logo used for display purposes.</p>
            </div>
            <div class="company-client-modal__grid">
              <div class="ds-field company-client-modal__field-full">
                <label class="ds-field__label">Company Logo</label>
                <b-form-file
                  @input="onChangeFile"
                  v-model="selected_file.company_logo"
                  :state="Boolean(selected_file.company_logo)"
                  class="company-client-modal__file-input border-1 p-2"
                  accept=".png, .jpg, .jpeg"
                  placeholder="Choose a logo or drop it here..."
                  drop-placeholder="Drop logo here..."
                />
                <div v-if="form.company_logo_base64" class="company-client-modal__logo-preview">
                  <img :src="form.company_logo_base64" alt="Company logo preview" />
                </div>
              </div>
            </div>
          </section>
        </b-form>

        <div class="company-client-modal__actions">
          <button class="ds-btn ds-btn--secondary ds-btn--sm" @click="onHide">Cancel</button>
          <button class="ds-btn ds-btn--primary ds-btn--sm" :disabled="loading.submit" @click="onSubmit">
            {{ mode === "edit" ? "Update Company Client" : "Create Company Client" }}
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import moment from "moment";
import { GET_CITY, GET_PROVINCE } from "@/store/modules/companies";
import popupErrorMessages from "@/library/popup-error-messages";
import { CREATE_COMPANY_CLIENT_RECORD, UPDATE_COMPANY_CLIENT_RECORD } from "../../../store/modules/company_clients";
import base64File from "@/library/base64File";

const createDefaultForm = () => ({
  id: null,
  vendor_company_id: 0,
  name: "",
  email: "",
  phone: "",
  address: "",
  logo_file: null,
  logo: null,
  company_logo_base64: "",
  industry: "",
  province: "",
  account_manager: "",
  city: "",
  category: "",
  latitude: 0,
  longitude: 0,
  location: "",
  status: "",
  pic_name: "",
  pic_email: "",
  pic_phone_number: "",
  max_balance_disbursement: 0,
  pks_number: "",
  pks_start_date: moment().format("YYYY-MM-DD"),
  pks_end_date: moment().add(7, "days").format("YYYY-MM-DD"),
});

export default {
  name: "CreateCompanyClient",
  props: {
    isShown: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: "create",
    },
    clientData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: {
        submit: false,
        company: false,
      },
      show: false,
      selected_file: {
        company_logo: null,
      },
      endDateKey: 0,
      companyCategoryOptions: [
        { value: "ACTIVE", text: "ACTIVE" },
        { value: "NOT_ACTIVE", text: "NOT ACTIVE" },
        { value: "WAITING_APPROVAL", text: "WAITING APPROVAL" },
        { value: "DELETED", text: "DELETED" },
      ],
      form: createDefaultForm(),
      datePickerStartOptions: {
        disabledDate: (date) => moment(date).isBefore(moment(), "days"),
      },
      datePickerEndOptions: {
        disabledDate: (date) => moment(date).isBefore(moment(), "days"),
      },
      city: {
        rows: [],
      },
      province: {
        rows: [],
      },
    };
  },
  computed: {
    filteredCities() {
      if (!this.form.province) {
        return this.city.rows;
      }

      return this.city.rows.filter(
        (item) => item.province_name === this.form.province || item.province_id === this.form.province
      );
    },
  },
  methods: {
    showFormError(error) {
      const responseData = error?.response?.data || error;
      const fallbackMessage =
        responseData?.error?.message || responseData?.message || error?.message || "Failed to process company client request.";

      return popupErrorMessages(responseData).catch(() => {
        this.$message({
          title: "Failed",
          type: "error",
          message: fallbackMessage,
        });
      });
    },
    async onChangeFile() {
      if (!this.selected_file.company_logo) {
        this.form.company_logo_base64 = "";
        return;
      }

      try {
        this.form.company_logo_base64 = await base64File(this.selected_file.company_logo);
      } catch (error) {
        this.form.company_logo_base64 = "";
      }
    },
    updateEndDatePickerOptions() {
      if (!this.form.pks_start_date) {
        return;
      }

      const selectedStartDate = moment(this.form.pks_start_date, "YYYY-MM-DD");
      this.datePickerEndOptions = {
        disabledDate: (date) => {
          const selectedDate = moment(date);
          return selectedDate.isBefore(moment(), "days") || selectedDate.isBefore(selectedStartDate, "day");
        },
      };
      this.endDateKey += 1;
    },
    async getProvince({ search_keyword = "" } = {}) {
      this.loading.company = true;
      try {
        await this.$store.dispatch(GET_PROVINCE, { search_keyword });
        const { rows = [] } = this.$store.getters.province || {};
        this.province.rows = rows;
      } finally {
        this.loading.company = false;
      }
    },
    async getCity({ search_keyword = "" } = {}) {
      this.loading.company = true;
      try {
        await this.$store.dispatch(GET_CITY, { search_keyword });
        const { rows = [] } = this.$store.getters.city || {};
        this.city.rows = rows;
      } finally {
        this.loading.company = false;
      }
    },
    resetForm() {
      this.form = createDefaultForm();
      this.selected_file = {
        company_logo: null,
      };
    },
    hydrateForm(data = {}) {
      this.form = {
        ...createDefaultForm(),
        ...data,
        company_logo_base64: data?.company_logo_base64 || data?.logo || "",
        max_balance_disbursement: data?.company_policy?.max_balance_disbursement ?? data?.max_balance_disbursement ?? 0,
        pks_number: data?.company_agreement?.pks_number ?? data?.pks_number ?? "",
        pks_start_date: data?.company_agreement?.pks_start_date
          ? moment(data.company_agreement.pks_start_date).format("YYYY-MM-DD")
          : data?.pks_start_date || createDefaultForm().pks_start_date,
        pks_end_date: data?.company_agreement?.pks_end_date
          ? moment(data.company_agreement.pks_end_date).format("YYYY-MM-DD")
          : data?.pks_end_date || createDefaultForm().pks_end_date,
      };
      this.updateEndDatePickerOptions();
    },
    onHide() {
      this.resetForm();
      this.$emit("onHide");
    },
    async onSubmit() {
      this.loading.submit = true;
      try {
        if (this.mode === "create") {
          const requestBody = {
            vendor_company_id: parseInt(this.$route.params.id, 10) || 0,
            name: this.form.name,
            phone: this.form.phone,
            email: this.form.email,
            logo: this.form.company_logo_base64 || "https://nos.wjv-1.neo.id/v-apps/mybenefit/intro-new.png",
            category: this.form.category,
            industry: this.form.industry,
            account_manager: this.form.account_manager,
            pic_name: this.form.pic_name,
            pic_email: this.form.pic_email,
            pic_phone_number: this.form.pic_phone_number,
            address: this.form.address,
            province: this.form.province || "SUMATERA UTARA",
            city: this.form.city || "MEDAN",
            latitude: Number(this.form.latitude) || 0,
            longitude: Number(this.form.longitude) || 0,
            status: this.form.status,
            company_agreement: {
              pks_number: this.form.pks_number,
              pks_start_date: this.form.pks_start_date,
              pks_end_date: this.form.pks_end_date,
            },
            company_policy: {
              max_balance_disbursement: parseInt(this.form.max_balance_disbursement, 10) || 0,
              alert_method: "email",
              total_limit_percentage: 100,
            },
          };

          await this.$store.dispatch(CREATE_COMPANY_CLIENT_RECORD, requestBody);
          this.$message({
            title: "Success",
            type: "success",
            message: "Company Client successfully created",
          });
        } else {
          const requestBody = {
            name: this.form.name,
            phone: this.form.phone,
            email: this.form.email,
            logo: this.form.company_logo_base64 || "https://nos.wjv-1.neo.id/v-apps/mybenefit/intro-new.png",
            category: this.form.category,
            industry: this.form.industry,
            account_manager: this.form.account_manager,
            pic_name: this.form.pic_name,
            pic_email: this.form.pic_email,
            pic_phone_number: this.form.pic_phone_number,
            address: this.form.address,
            province: this.form.province,
            city: this.form.city,
            latitude: Number(this.form.latitude) || 0,
            longitude: Number(this.form.longitude) || 0,
            location: this.form.location,
          };

          await this.$store.dispatch(UPDATE_COMPANY_CLIENT_RECORD, {
            id: this.form.id,
            ...requestBody,
          });

          this.$message({
            title: "Success",
            type: "success",
            message: "Company Client successfully updated",
          });
        }

        this.onHide();
      } catch (err) {
        await this.showFormError(err);
      } finally {
        this.loading.submit = false;
      }
    },
  },
  async mounted() {
    await this.getProvince();
    await this.getCity();
    if (this.mode === "edit" && this.clientData) {
      this.hydrateForm(this.clientData);
    }
  },
  watch: {
    isShown(val) {
      this.show = val;
      if (!val) {
        return;
      }

      this.getProvince();
      this.getCity();

      if (this.mode === "edit" && this.clientData) {
        this.hydrateForm(this.clientData);
        return;
      }

      this.resetForm();
    },
  },
};
</script>

<style scoped>
.company-client-modal__intro {
  margin-bottom: 18px;
  padding: 14px 16px;
  border-radius: 14px;
  background: linear-gradient(180deg, rgba(32, 107, 196, 0.08), rgba(32, 107, 196, 0.04));
}

.company-client-modal__eyebrow {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #206bc4;
  margin-bottom: 6px;
}

.company-client-modal__lede {
  font-size: 13px;
  color: #4b5563;
}

.company-client-modal__section + .company-client-modal__section {
  margin-top: 18px;
}

.company-client-modal__section-head {
  margin-bottom: 12px;
}

.company-client-modal__section-head h4 {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #182433;
}

.company-client-modal__section-head p {
  margin: 4px 0 0;
  font-size: 12px;
  color: #656d77;
}

.company-client-modal__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.company-client-modal__field-full {
  grid-column: 1 / -1;
}

.company-client-modal__file-input {
  width: 100%;
}

.company-client-modal__logo-preview {
  margin-top: 12px;
  padding: 12px;
  border: 1px dashed #dce1e7;
  border-radius: 12px;
  background: #f8fafc;
  display: flex;
  justify-content: center;
}

.company-client-modal__logo-preview img {
  max-width: 100%;
  max-height: 140px;
  object-fit: contain;
}

.company-client-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 22px;
}

@media (max-width: 768px) {
  .company-client-modal__grid {
    grid-template-columns: 1fr;
  }
}
</style>
